function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
import { fetchConversationResponseToConversation } from '@rocketlane/rocket-llm';
import { getAnalyticsSendMessagePayload, getFeedbackPayload } from 'api/ai-chat/utils';
import { CHAT_ATTACHMENT_MAX_COUNT, CHAT_ATTACHMENT_STATUS_POLL_MS, postFileToS3Presigned, sleep } from 'api/chat/base-chat';
import { HttpClient } from 'api/http-client';
import { getMountedPanelsForChat } from 'components/organisms/ai-analytics/panel/mounted-panels-store';
import { CONVERSATION_SERVICE_URL } from 'config/environment';
import { AccountFeature, isAccountFeatureEnabled } from 'hooks/use-account-feature';
import { getFileContentType } from 'utils/files';
export class AiAnalystsHttpClient extends HttpClient {
    async sendMessage(params) {
        var _params_mountedPanels;
        const openuiEnabled = isAccountFeatureEnabled(AccountFeature.Openui);
        const mountedPanels = openuiEnabled ? (_params_mountedPanels = params.mountedPanels) !== null && _params_mountedPanels !== void 0 ? _params_mountedPanels : getMountedPanelsForChat(params.conversationId).map((p)=>({
                conversationId: params.conversationId,
                ...p
            })) : undefined;
        const payload = getAnalyticsSendMessagePayload({
            ...params,
            mountedPanels
        });
        const data = await this.post('/chat', payload);
        return data;
    }
    async getAllAnalyticsConversations(params) {
        const { offset, limit, agent_name } = params;
        if (agent_name == null || String(agent_name).trim() === '') {
            throw new Error('getAllAnalyticsConversations requires a non-empty agent_name to avoid mixing agents’ conversation histories');
        }
        const config = {
            params: {
                page: offset === 0 ? '1' : (offset / limit + 1).toString(),
                page_size: limit.toString(),
                agent_name: agent_name.trim()
            }
        };
        const data = await this.get('/chat/conversations', config);
        return data;
    }
    async getAnalyticsConversation(params) {
        try {
            const config = {
                params: {
                    conversation_id: params.conversation_id,
                    page: params.page,
                    page_size: params.page_size,
                    include_token: params.include_token
                }
            };
            const responseData = await this.get(`/chat/conversations/${params.conversation_id}`, config);
            return fetchConversationResponseToConversation(responseData);
        } catch (error) {
            var _error_response_data, _error_response;
            const errorMessage = (error === null || error === void 0 ? void 0 : (_error_response = error.response) === null || _error_response === void 0 ? void 0 : (_error_response_data = _error_response.data) === null || _error_response_data === void 0 ? void 0 : _error_response_data.message) || (error === null || error === void 0 ? void 0 : error.message) || 'Error fetching conversation';
            throw new Error(errorMessage, {
                cause: error
            });
        }
    }
    async deleteAnalyticsConversation(conversationId) {
        await this.delete(`/chat/conversations/${conversationId}`);
    }
    // eslint-disable-next-line class-methods-use-this
    async getAnalysts() {
        try {
            const data = await this.get('/ai-analysts');
            return data;
        } catch (error) {
            var _error_response;
            const errorMessage = (error === null || error === void 0 ? void 0 : (_error_response = error.response) === null || _error_response === void 0 ? void 0 : _error_response.statusText) || (error === null || error === void 0 ? void 0 : error.message) || 'Failed to fetch analytics agents';
            throw new Error(errorMessage, {
                cause: error
            });
        }
    }
    async getSignalAgents() {
        const data = await this.get('/signal-agent');
        if (!data) {
            throw new Error('Failed to fetch signals agents');
        }
        return data;
    }
    async dispatchIntegrations(payload) {
        await this.post('/integrations/dispatch', payload);
    }
    async sendFeedback(params) {
        const payload = getFeedbackPayload(params);
        await this.post('/chat/feedback', payload);
    }
    async updateFeedback(params) {
        const payload = getFeedbackPayload(params);
        await this.put('/chat/feedback', payload);
    }
    async convertMarkdownToPdf(params) {
        const response = await this.post('/agent-exports/convert-markdown-to-pdf', params);
        return response.data;
    }
    async uploadFile(file) {
        const contentType = getFileContentType(file);
        const { upload_url, file_key } = await this.post(`${this.BASEURL}/agent-attachments/get_upload_url`, {
            file_name: file.name,
            content_type: contentType
        });
        const putHeaders = {};
        putHeaders['Content-Type'] = contentType;
        await this.put(upload_url, file, {
            headers: putHeaders
        });
        const { s3_url } = await this.post(`${this.BASEURL}/agent-attachments/confirm_upload?file_key=${encodeURIComponent(file_key)}`, undefined);
        return {
            s3_url,
            fileKey: file_key
        };
    }
    getAttachmentPresignedUrls(conversationId, params) {
        return this.get(`/chat/attachment/${conversationId}`, {
            params
        });
    }
    getAttachmentStatus(conversationId, fileKey) {
        return this.get(`/chat/attachment/${conversationId}/status`, {
            params: {
                file_key: fileKey
            }
        });
    }
    async waitForAttachmentUploaded(conversationId, fileKey, timeoutMs) {
        const deadline = Date.now() + timeoutMs;
        // Poll sequentially — each status check must wait for the previous result.
        while(Date.now() < deadline){
            const status = await this.getAttachmentStatus(conversationId, fileKey);
            if (status.uploaded) {
                return status;
            }
            await sleep(CHAT_ATTACHMENT_STATUS_POLL_MS);
        }
        throw new Error(`Timed out waiting for attachment upload status for ${fileKey}`);
    }
    /**
   * Presign → S3 POST → poll status until uploaded.
   * Returns file_key values to send in chat message sections.
   */ async uploadChatAttachments(conversationId, files) {
        var _presigned_presigned_urls, _presigned_expiration_seconds;
        if (files.length === 0) {
            return [];
        }
        if (files.length > CHAT_ATTACHMENT_MAX_COUNT) {
            throw new Error(`Cannot upload more than ${CHAT_ATTACHMENT_MAX_COUNT} files at once`);
        }
        const presigned = await this.getAttachmentPresignedUrls(conversationId, {
            count: files.length
        });
        const slots = (_presigned_presigned_urls = presigned.presigned_urls) !== null && _presigned_presigned_urls !== void 0 ? _presigned_presigned_urls : [];
        if (slots.length < files.length) {
            throw new Error('Not enough presigned upload URLs returned');
        }
        await Promise.all(files.map(async (file, index)=>{
            var _slot_fields;
            const slot = slots[index];
            if (!slot) {
                throw new Error(`Missing presigned URL for file ${file.name}`);
            }
            if (typeof presigned.max_file_size_bytes === 'number' && file.size > presigned.max_file_size_bytes) {
                throw new Error(`${file.name} exceeds the ${presigned.max_file_size_mb}MB size limit`);
            }
            if (typeof presigned.min_file_size_bytes === 'number' && file.size < presigned.min_file_size_bytes) {
                throw new Error(`${file.name} is below the minimum file size`);
            }
            await postFileToS3Presigned(slot.url, (_slot_fields = slot.fields) !== null && _slot_fields !== void 0 ? _slot_fields : {}, file);
        }));
        const timeoutMs = Math.max(((_presigned_expiration_seconds = presigned.expiration_seconds) !== null && _presigned_expiration_seconds !== void 0 ? _presigned_expiration_seconds : 60) * 1000, CHAT_ATTACHMENT_STATUS_POLL_MS * 2);
        return Promise.all(files.map(async (file, index)=>{
            var _slots_index;
            const fileKey = (_slots_index = slots[index]) === null || _slots_index === void 0 ? void 0 : _slots_index.key;
            if (!fileKey) {
                throw new Error(`Missing file key for ${file.name}`);
            }
            await this.waitForAttachmentUploaded(conversationId, fileKey, timeoutMs);
            return {
                fileKey,
                fileName: file.name
            };
        }));
    }
    async getAttachmentUploadUrl(conversationId) {
        const response = await this.get(`/chat/attachment/${conversationId}`);
        return response.presigned_urls[0];
    }
    async uploadAttachmentToS3(uploadUrl, fields, file) {
        const formData = new FormData();
        Object.entries(fields).forEach(([k, v])=>formData.append(k, v));
        formData.append('file', file);
        await fetch(uploadUrl, {
            method: 'POST',
            body: formData
        });
    }
    async checkAttachmentStatus(conversationId, fileKey) {
        const response = await this.get(`/chat/attachment/${conversationId}/status?file_key=${encodeURIComponent(fileKey)}`);
        return response.uploaded;
    }
    async patchMessageSectionValue(conversationId, sectionId, valuePatch) {
        await this.patch(`/chat/conversations/${conversationId}/message_sections/${sectionId}/value`, valuePatch);
    }
}
_define_property(AiAnalystsHttpClient, "getStreamUrl", (streamId)=>{
    return `${CONVERSATION_SERVICE_URL}/api/v1/chat/stream/${streamId}`;
});
export const aiAnalystsHttpClient = new AiAnalystsHttpClient();
